import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from './ui/sheet';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Save, X, Building2, Users as UsersIcon, TrendingUp, MapPin } from 'lucide-react';
import { toast } from 'sonner';

const Section = ({ icon: Icon, title, children }) => (
  <div className="space-y-3">
    <div className="flex items-center gap-2 text-sm font-bold text-slate-800">
      <Icon className="h-4 w-4 text-blue-600" />
      <span>{title}</span>
    </div>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">{children}</div>
  </div>
);

const Field = ({ label, name, value, onChange, type = 'text', testid }) => (
  <div className="space-y-1.5">
    <Label className="text-xs text-slate-600">{label}</Label>
    <Input
      type={type}
      value={value || ''}
      onChange={(e) => onChange(name, e.target.value)}
      data-testid={testid || `edit-company-${name}`}
      className="h-9 text-sm"
    />
  </div>
);

export default function EditCompanyInfoSheet({ open, onOpenChange, companyInfo, onSaved }) {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setForm({
        nom: '',
        description: '',
        forme_juridique: '',
        rccm: '',
        nif: '',
        date_creation: '',
        pays: '',
        ville: '',
        adresse: '',
        site_web: '',
        contact_principal: '',
        contact_fonction: '',
        telephone: '',
        telephone_fixe: '',
        email: '',
        email_secondaire: '',
        type_client: 'Entreprise',
        statut_commercial: 'Actif',
        responsable_commercial: '',
        source: '',
        devise: 'FCFA',
        conditions_paiement: '',
        plafond_credit: '',
        remise: '',
        adresse_facturation: '',
        slogan: '',
        facebook: '',
        linkedin: '',
        twitter: '',
        ...(companyInfo || {}),
      });
    }
  }, [open, companyInfo]);

  const setField = (name, value) => setForm((f) => ({ ...f, [name]: value }));

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!form.nom || !form.nom.trim()) {
      toast.error('La raison sociale est requise');
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, id: 'sesam-main' };
      await axios.put(`${process.env.REACT_APP_BACKEND_URL}/api/company`, payload);
      toast.success("Informations de l'entreprise enregistrées");
      onOpenChange(false);
      onSaved && onSaved(payload);
    } catch (err) {
      console.error(err);
      const msg = err?.response?.data?.detail || "Erreur lors de l'enregistrement";
      toast.error(typeof msg === 'string' ? msg : "Erreur lors de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-[720px] overflow-y-auto" data-testid="edit-company-sheet">
        <SheetHeader>
          <SheetTitle className="text-xl font-bold text-[#1C2A46]">Modifier les informations de l'entreprise</SheetTitle>
          <SheetDescription>Mettez à jour les informations légales, contacts et données commerciales.</SheetDescription>
        </SheetHeader>

        <form onSubmit={handleSubmit} className="mt-5 space-y-6">
          <Section icon={Building2} title="Informations générales">
            <Field label="Raison sociale *" name="nom" value={form.nom} onChange={setField} />
            <Field label="Forme juridique / Activité" name="forme_juridique" value={form.forme_juridique} onChange={setField} />
            <Field label="Numéro d'identification (RCCM)" name="rccm" value={form.rccm} onChange={setField} />
            <Field label="Numéro contribuable (NIF)" name="nif" value={form.nif} onChange={setField} />
            <Field label="Date de création" name="date_creation" value={form.date_creation} onChange={setField} />
            <Field label="Pays" name="pays" value={form.pays} onChange={setField} />
            <Field label="Ville" name="ville" value={form.ville} onChange={setField} />
            <Field label="Site web" name="site_web" value={form.site_web} onChange={setField} />
            <div className="sm:col-span-2 space-y-1.5">
              <Label className="text-xs text-slate-600">Adresse</Label>
              <Textarea
                value={form.adresse || ''}
                onChange={(e) => setField('adresse', e.target.value)}
                data-testid="edit-company-adresse"
                rows={2}
                className="text-sm"
              />
            </div>
            <div className="sm:col-span-2 space-y-1.5">
              <Label className="text-xs text-slate-600">Slogan</Label>
              <Input
                value={form.slogan || ''}
                onChange={(e) => setField('slogan', e.target.value)}
                data-testid="edit-company-slogan"
                className="h-9 text-sm"
              />
            </div>
          </Section>

          <Section icon={UsersIcon} title="Informations de contact">
            <Field label="Contact principal" name="contact_principal" value={form.contact_principal} onChange={setField} />
            <Field label="Fonction" name="contact_fonction" value={form.contact_fonction} onChange={setField} />
            <Field label="Téléphone mobile" name="telephone" value={form.telephone} onChange={setField} />
            <Field label="Téléphone fixe" name="telephone_fixe" value={form.telephone_fixe} onChange={setField} />
            <Field label="Email principal" name="email" value={form.email} onChange={setField} type="email" />
            <Field label="Autre email" name="email_secondaire" value={form.email_secondaire} onChange={setField} type="email" />
          </Section>

          <Section icon={TrendingUp} title="Informations commerciales">
            <div className="space-y-1.5">
              <Label className="text-xs text-slate-600">Type de client</Label>
              <Select value={form.type_client || 'Entreprise'} onValueChange={(v) => setField('type_client', v)}>
                <SelectTrigger className="h-9 text-sm" data-testid="edit-company-type_client"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Entreprise">Entreprise</SelectItem>
                  <SelectItem value="Particulier">Particulier</SelectItem>
                  <SelectItem value="Administration">Administration</SelectItem>
                  <SelectItem value="ONG">ONG</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-slate-600">Statut commercial</Label>
              <Select value={form.statut_commercial || 'Actif'} onValueChange={(v) => setField('statut_commercial', v)}>
                <SelectTrigger className="h-9 text-sm" data-testid="edit-company-statut_commercial"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Actif">Actif</SelectItem>
                  <SelectItem value="Inactif">Inactif</SelectItem>
                  <SelectItem value="Prospect">Prospect</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Field label="Responsable commercial" name="responsable_commercial" value={form.responsable_commercial} onChange={setField} />
            <Field label="Source" name="source" value={form.source} onChange={setField} />
            <Field label="Devise" name="devise" value={form.devise} onChange={setField} />
            <Field label="Conditions de paiement" name="conditions_paiement" value={form.conditions_paiement} onChange={setField} />
            <Field label="Plafond de crédit" name="plafond_credit" value={form.plafond_credit} onChange={setField} />
            <Field label="Remise par défaut" name="remise" value={form.remise} onChange={setField} />
          </Section>

          <Section icon={MapPin} title="Adresses & réseaux">
            <div className="sm:col-span-2 space-y-1.5">
              <Label className="text-xs text-slate-600">Adresse de facturation</Label>
              <Textarea
                value={form.adresse_facturation || ''}
                onChange={(e) => setField('adresse_facturation', e.target.value)}
                data-testid="edit-company-adresse_facturation"
                rows={2}
                className="text-sm"
              />
            </div>
            <Field label="Facebook" name="facebook" value={form.facebook} onChange={setField} />
            <Field label="LinkedIn" name="linkedin" value={form.linkedin} onChange={setField} />
            <Field label="Twitter" name="twitter" value={form.twitter} onChange={setField} />
          </Section>

          <div className="flex items-center justify-end gap-2 pt-4 border-t border-slate-100">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} data-testid="edit-company-cancel">
              <X className="h-4 w-4 mr-1" /> Annuler
            </Button>
            <Button type="submit" disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="edit-company-save">
              <Save className="h-4 w-4 mr-1" /> {saving ? 'Enregistrement…' : 'Enregistrer'}
            </Button>
          </div>
        </form>
      </SheetContent>
    </Sheet>
  );
}
